import React, { useState, useEffect, useCallback } from "react";
import {
  Globe,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  AlertCircle,
  RefreshCw,
  UserPlus,
  UserMinus,
} from "lucide-react";
import { AUTH_URL } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";

export default function FederationSettings() {
  const { token } = useAuth();
  const [federations, setFederations] = useState([]);
  const [allFacilities, setAllFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Expanded card state: federationId → { members, loadingMembers }
  const [expanded, setExpanded] = useState({});

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    slug: "",
    description: "",
  });
  const [creating, setCreating] = useState(false);

  // Edit state: federationId → form fields
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(null);

  // Delete confirm state
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Add facility state: federationId → selected facility_id
  const [addFacilityFor, setAddFacilityFor] = useState(null);
  const [addFacilityVal, setAddFacilityVal] = useState("");
  const [addingFacility, setAddingFacility] = useState(false);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const loadFederations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${AUTH_URL}/admin/federations`, {
        headers: authHeaders,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setFederations(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line

  const loadAllFacilities = useCallback(async () => {
    try {
      const r = await fetch(`${AUTH_URL}/admin/facilities`, {
        headers: authHeaders,
      });
      if (!r.ok) return;
      const data = await r.json();
      setAllFacilities(Array.isArray(data) ? data : data.facilities || []);
    } catch (_) {}
  }, [token]); // eslint-disable-line

  useEffect(() => {
    loadFederations();
    loadAllFacilities();
  }, [loadFederations, loadAllFacilities]);

  const loadMembers = async (fedId) => {
    setExpanded((prev) => ({
      ...prev,
      [fedId]: { ...(prev[fedId] || {}), loadingMembers: true },
    }));
    try {
      const r = await fetch(
        `${AUTH_URL}/admin/federations/${fedId}/facilities`,
        { headers: authHeaders },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const members = await r.json();
      setExpanded((prev) => ({
        ...prev,
        [fedId]: { members, loadingMembers: false },
      }));
    } catch (e) {
      setExpanded((prev) => ({
        ...prev,
        [fedId]: { members: [], loadingMembers: false },
      }));
    }
  };

  const toggleExpand = (fedId) => {
    if (expanded[fedId]) {
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[fedId];
        return next;
      });
    } else {
      loadMembers(fedId);
    }
  };

  const handleCreateName = (name) => {
    setCreateForm((prev) => ({
      ...prev,
      name,
      slug: name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, ""),
    }));
  };

  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.slug.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const r = await fetch(`${AUTH_URL}/admin/federations`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(createForm),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      const created = await r.json();
      setFederations((prev) => [...prev, { ...created, member_count: 0 }]);
      setCreateForm({ name: "", slug: "", description: "" });
      setShowCreate(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (fed) => {
    setEditingId(fed.id);
    setEditForm({
      name: fed.name,
      slug: fed.slug,
      description: fed.description || "",
      is_active: fed.is_active,
    });
  };

  const handleSaveEdit = async () => {
    setSaving(editingId);
    setError(null);
    try {
      const r = await fetch(`${AUTH_URL}/admin/federations/${editingId}`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify(editForm),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      const updated = await r.json();
      setFederations((prev) =>
        prev.map((f) => (f.id === editingId ? { ...f, ...updated } : f)),
      );
      setEditingId(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (fed) => {
    setDeleting(fed.id);
    setError(null);
    try {
      const r = await fetch(`${AUTH_URL}/admin/federations/${fed.id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setFederations((prev) => prev.filter((f) => f.id !== fed.id));
      setConfirmDelete(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(null);
    }
  };

  const handleAddFacility = async (fedId) => {
    if (!addFacilityVal) return;
    setAddingFacility(true);
    try {
      const r = await fetch(
        `${AUTH_URL}/admin/federations/${fedId}/facilities`,
        {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({ facility_id: addFacilityVal }),
        },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setFederations((prev) =>
        prev.map((f) =>
          f.id === fedId
            ? { ...f, member_count: (f.member_count || 0) + 1 }
            : f,
        ),
      );
      await loadMembers(fedId);
      setAddFacilityFor(null);
      setAddFacilityVal("");
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingFacility(false);
    }
  };

  const handleRemoveFacility = async (fedId, facilityId) => {
    try {
      const r = await fetch(
        `${AUTH_URL}/admin/federations/${fedId}/facilities/${facilityId}`,
        { method: "DELETE", headers: authHeaders },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setFederations((prev) =>
        prev.map((f) =>
          f.id === fedId
            ? { ...f, member_count: Math.max(0, (f.member_count || 1) - 1) }
            : f,
        ),
      );
      setExpanded((prev) => ({
        ...prev,
        [fedId]: {
          ...prev[fedId],
          members: (prev[fedId]?.members || []).filter(
            (m) => m.facility_id !== facilityId,
          ),
        },
      }));
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-5 h-5 text-slate-400 animate-spin mr-2" />
        <span className="text-slate-400">Loading federations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-50 rounded-lg flex-shrink-0">
            <Globe className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-slate-800">
              Federation Networks
            </h2>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              Federations are named groups of LDs that share anonymized
              aggregate signals. Each federation can configure its own topic
              taxonomy overrides. Examples: GOP districts, Real Estate
              associations, TPUSA chapters.
            </p>
          </div>
          <button
            onClick={() => {
              setShowCreate((v) => !v);
              setError(null);
            }}
            className="flex items-center gap-1.5 text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            New Federation
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-indigo-200 p-5 space-y-4">
          <h3 className="font-semibold text-slate-700 text-sm">
            Create Federation
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">
                Name *
              </label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => handleCreateName(e.target.value)}
                placeholder="GOP Districts"
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">
                Slug *
              </label>
              <input
                type="text"
                value={createForm.slug}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, slug: e.target.value }))
                }
                placeholder="gop_districts"
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 font-mono"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              Description
            </label>
            <input
              type="text"
              value={createForm.description}
              onChange={(e) =>
                setCreateForm((p) => ({ ...p, description: e.target.value }))
              }
              placeholder="Optional description"
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setShowCreate(false);
                setCreateForm({ name: "", slug: "", description: "" });
              }}
              className="text-sm px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={
                creating || !createForm.name.trim() || !createForm.slug.trim()
              }
              className="text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-colors"
            >
              {creating ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 flex items-center gap-2 text-sm text-rose-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Federation cards */}
      {federations.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-sm text-slate-400">
          No federations yet. Create one above.
        </div>
      ) : (
        <div className="space-y-3">
          {federations.map((fed) => {
            const isExpanded = !!expanded[fed.id];
            const expData = expanded[fed.id] || {};
            const isEditing = editingId === fed.id;

            return (
              <div
                key={fed.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
              >
                {/* Card header */}
                <div className="px-5 py-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="grid grid-cols-3 gap-3">
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) =>
                            setEditForm((p) => ({
                              ...p,
                              name: e.target.value,
                            }))
                          }
                          className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="Name"
                        />
                        <input
                          type="text"
                          value={editForm.slug}
                          onChange={(e) =>
                            setEditForm((p) => ({
                              ...p,
                              slug: e.target.value,
                            }))
                          }
                          className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="slug"
                        />
                        <input
                          type="text"
                          value={editForm.description}
                          onChange={(e) =>
                            setEditForm((p) => ({
                              ...p,
                              description: e.target.value,
                            }))
                          }
                          className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          placeholder="Description"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">
                            {fed.name}
                          </span>
                          <span className="font-mono text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                            {fed.slug}
                          </span>
                          {fed.is_active ? (
                            <span className="text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">
                              Active
                            </span>
                          ) : (
                            <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                              Inactive
                            </span>
                          )}
                        </div>
                        {fed.description && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {fed.description}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-0.5">
                          {fed.member_count ?? 0} member
                          {fed.member_count !== 1 ? "s" : ""}
                        </p>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isEditing ? (
                      <>
                        <button
                          onClick={handleSaveEdit}
                          disabled={saving === fed.id}
                          className="p-1.5 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-40"
                          title="Save"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1.5 text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(fed)}
                          className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(fed)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleExpand(fed.id)}
                          className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                          title={isExpanded ? "Collapse" : "Manage members"}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded members panel */}
                {isExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50 px-5 py-4">
                    {expData.loadingMembers ? (
                      <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Loading members...
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                            Member Facilities
                          </p>
                          <button
                            onClick={() => {
                              setAddFacilityFor((prev) =>
                                prev === fed.id ? null : fed.id,
                              );
                              setAddFacilityVal("");
                            }}
                            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                            Add Facility
                          </button>
                        </div>

                        {addFacilityFor === fed.id && (
                          <div className="flex gap-2 mb-3">
                            <select
                              value={addFacilityVal}
                              onChange={(e) =>
                                setAddFacilityVal(e.target.value)
                              }
                              className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            >
                              <option value="">Select facility...</option>
                              {allFacilities
                                .filter(
                                  (f) =>
                                    !(expData.members || []).some(
                                      (m) => m.facility_id === f.facility_id,
                                    ),
                                )
                                .map((f) => (
                                  <option
                                    key={f.facility_id}
                                    value={f.facility_id}
                                  >
                                    {f.display_name || f.facility_id}
                                  </option>
                                ))}
                            </select>
                            <button
                              onClick={() => handleAddFacility(fed.id)}
                              disabled={!addFacilityVal || addingFacility}
                              className="text-sm px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-colors"
                            >
                              {addingFacility ? "Adding..." : "Add"}
                            </button>
                            <button
                              onClick={() => {
                                setAddFacilityFor(null);
                                setAddFacilityVal("");
                              }}
                              className="text-sm px-3 py-1.5 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        )}

                        {(expData.members || []).length === 0 ? (
                          <p className="text-xs text-slate-400 py-2">
                            No members yet.
                          </p>
                        ) : (
                          <div className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white overflow-hidden">
                            {(expData.members || []).map((m) => (
                              <div
                                key={m.facility_id}
                                className="flex items-center justify-between px-4 py-2.5"
                              >
                                <div>
                                  <span className="text-sm font-medium text-slate-700">
                                    {m.display_name || m.facility_id}
                                  </span>
                                  {m.state && (
                                    <span className="ml-2 text-xs text-slate-400">
                                      {m.state}
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() =>
                                    handleRemoveFacility(fed.id, m.facility_id)
                                  }
                                  className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                                  title="Remove from federation"
                                >
                                  <UserMinus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-rose-50 rounded-lg">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <h3 className="font-semibold text-slate-800">
                Delete federation?
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              <span className="font-medium">{confirmDelete.name}</span> will be
              permanently deleted along with all facility memberships and topic
              overrides.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={deleting === confirmDelete.id}
                className="flex-1 bg-rose-600 text-white text-sm font-medium py-2 rounded-lg hover:bg-rose-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 bg-slate-100 text-slate-700 text-sm font-medium py-2 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
